import React, { useEffect, useState } from 'react';

const CampaignNotificationPopup = ({ campaign, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation after component mounts
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 300); // Wait for animation to complete
  };

  if (!campaign) return null;

  return (
    <div
      className={`fixed bottom-6 right-6 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden transition-all duration-300 ease-out z-50 ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
    >
      {/* Header with close button */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 flex justify-between items-center">
        <h3 className="text-white font-semibold text-sm flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Suchanzeigen-Details
        </h3>
        <button
          onClick={handleClose}
          className="text-white hover:text-gray-200 transition"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="p-4 max-h-[500px] overflow-y-auto">
        {/* Basic Info */}
        <div className="space-y-3 mb-4">
          <div>
            <p className="text-xs text-gray-500 uppercase font-semibold">Firma</p>
            <p className="text-sm font-bold text-gray-900">{campaign.company_name}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500 uppercase font-semibold">Standort</p>
              <p className="text-sm text-gray-900">{campaign.location}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-semibold">Ziel</p>
              <p className="text-sm text-gray-900">{campaign.goal}</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase font-semibold">Tagesbudget</p>
            <p className="text-lg font-bold text-green-600">{campaign.daily_budget} € / Tag</p>
          </div>
        </div>

        {/* Ad Texts */}
        <div className="border-t border-gray-200 pt-4 space-y-3">
          <h4 className="text-sm font-bold text-gray-900 flex items-center">
            <svg className="w-4 h-4 mr-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Anzeigentexte
          </h4>

          {/* Headlines */}
          <div className="bg-blue-50 p-3 rounded border border-blue-200">
            <div className="flex justify-between items-center mb-1">
              <p className="text-xs font-semibold text-blue-900">Headline 1</p>
              {campaign.headline1 && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(campaign.headline1);
                    window.showToast('📋 Headline 1 kopiert!', 'success');
                  }}
                  className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                  title="Kopieren"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              )}
            </div>
            <p className="text-sm font-semibold text-gray-900">
              {campaign.headline1 || <span className="text-gray-400 italic">Nicht festgelegt</span>}
            </p>
            {campaign.headline1 && (
              <p className="text-xs text-blue-700 mt-1">{campaign.headline1.length}/30 Zeichen</p>
            )}
          </div>

          <div className="bg-blue-50 p-3 rounded border border-blue-200">
            <div className="flex justify-between items-center mb-1">
              <p className="text-xs font-semibold text-blue-900">Headline 2</p>
              {campaign.headline2 && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(campaign.headline2);
                    window.showToast('📋 Headline 2 kopiert!', 'success');
                  }}
                  className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                  title="Kopieren"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              )}
            </div>
            <p className="text-sm font-semibold text-gray-900">
              {campaign.headline2 || <span className="text-gray-400 italic">Nicht festgelegt</span>}
            </p>
            {campaign.headline2 && (
              <p className="text-xs text-blue-700 mt-1">{campaign.headline2.length}/30 Zeichen</p>
            )}
          </div>

          <div className="bg-green-50 p-3 rounded border border-green-200">
            <div className="flex justify-between items-center mb-1">
              <p className="text-xs font-semibold text-green-900">Anzeigentext</p>
              {campaign.ad_text && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(campaign.ad_text);
                    window.showToast('📋 Anzeigentext kopiert!', 'success');
                  }}
                  className="p-1 bg-green-600 text-white rounded hover:bg-green-700 transition"
                  title="Kopieren"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              )}
            </div>
            <p className="text-sm text-gray-900">
              {campaign.ad_text || <span className="text-gray-400 italic">Nicht festgelegt</span>}
            </p>
            {campaign.ad_text && (
              <p className="text-xs text-green-700 mt-1">{campaign.ad_text.length}/90 Zeichen</p>
            )}
          </div>
        </div>

        {/* Google Suchanzeige Vorschau */}
        {(campaign.headline1 || campaign.headline2 || campaign.ad_text) && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            <p className="text-xs font-semibold text-gray-700 mb-2">Google Suchanzeige Vorschau</p>
            <div className="bg-gray-50 p-3 rounded border border-gray-200">
              <div className="text-xs text-green-700 mb-1">Anzeige</div>
              <div className="text-blue-600 text-sm font-semibold mb-1">
                {campaign.headline1 || 'Headline 1'} | {campaign.headline2 || 'Headline 2'}
              </div>
              <div className="text-green-700 text-xs mb-1">{campaign.location}</div>
              <div className="text-gray-900 text-xs">
                {campaign.ad_text || 'Anzeigentext'}
              </div>
            </div>
          </div>
        )}

        {/* Status */}
        <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
          <div>
            <p className="text-xs text-gray-500 uppercase font-semibold">Status</p>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
              campaign.status === 'aktiv' ? 'bg-green-100 text-green-800' :
              campaign.status === 'paused' ? 'bg-red-100 text-red-800' :
              campaign.status === 'in_bearbeitung' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {campaign.status}
            </span>
          </div>
          {campaign.payment_completed && (
            <div className="text-green-600 flex items-center text-sm">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Bezahlt
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignNotificationPopup;
